// "Templates as-is": plans without custom workflows (Starter, Sandbox) can
// still publish a copy of a Dibs template, as long as only its wording
// changed. This compares a draft's steps with its source template's, ignoring
// the wording an owner may edit. Everything else (which steps, their order and
// routing, triggers, conditions, delays, actions, AI outputs) must match.

import {
  withExplicitMessageRecipients,
  type JsonValue,
  type WorkflowDefinition,
  type WorkflowNode,
} from "./schema.ts";

/**
 * Wording per node type. `message` only counts when it is fixed text: a
 * message the AI writes from a Decision must keep that binding.
 */
export const TEMPLATE_WORDING_PARAMS: Readonly<Record<string, ReadonlySet<string>>> = {
  execution: new Set([
    "message",
    "reason",
    "noteTitle",
    "whatsappTemplateId",
    "whatsappTemplateName",
    "whatsappTemplateAuto",
  ]),
  decision: new Set(["goal", "tone", "mustAvoid", "mustInclude"]),
};

const WORDING = "<wording>";

function isStaticBinding(value: JsonValue | undefined): boolean {
  return !!value && typeof value === "object" && !Array.isArray(value) &&
    (value as Record<string, JsonValue>).source === "static";
}

function stepsOnly(node: WorkflowNode): Record<string, JsonValue> {
  const wording = TEMPLATE_WORDING_PARAMS[node.type] ?? new Set<string>();
  const parameters: Record<string, JsonValue> = {};
  for (const [key, value] of Object.entries(node.parameters ?? {})) {
    if (value === null || value === undefined) continue;
    if (!wording.has(key)) {
      parameters[key] = value;
    } else if (key === "message" && !isStaticBinding(value)) {
      parameters[key] = value;
    } else if (key === "message") {
      parameters[key] = { source: "static", value: WORDING };
    }
  }
  // label and description are the card's wording.
  return { id: node.id, type: node.type, parameters };
}

/** JSON with sorted object keys, so key order never counts as a change. */
function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableJson(v)}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

function stepsKey(def: WorkflowDefinition): string {
  const normalized = withExplicitMessageRecipients(def);
  return stableJson({
    nodes: normalized.nodes.map(stepsOnly),
    edges: normalized.edges ?? [],
  });
}

/** True when `draft` has the same steps as `template`, wording aside. */
export function sameStepsAsTemplate(
  draft: WorkflowDefinition,
  template: WorkflowDefinition,
): boolean {
  return stepsKey(draft) === stepsKey(template);
}

/**
 * Whether a parameter edit keeps a template as-is: wording only, and a
 * message stays fixed text. The builder uses this to lock everything else.
 */
export function isWordingEdit(
  node: WorkflowNode,
  key: string,
  value: unknown,
): boolean {
  if (!(TEMPLATE_WORDING_PARAMS[node.type]?.has(key) ?? false)) return false;
  if (key !== "message") return true;
  return isStaticBinding(node.parameters.message) &&
    isStaticBinding(value as JsonValue | undefined);
}

/** Whether `after` differs from `before` in wording only (title included). */
export function onlyWordingChanged(before: WorkflowNode, after: WorkflowNode): boolean {
  if (before.id !== after.id || before.type !== after.type) return false;
  const keys = new Set([
    ...Object.keys(before.parameters ?? {}),
    ...Object.keys(after.parameters ?? {}),
  ]);
  for (const key of keys) {
    const next = after.parameters?.[key];
    if (stableJson(before.parameters?.[key]) === stableJson(next)) continue;
    if (!isWordingEdit(before, key, next)) return false;
  }
  return true;
}
